import $ from "../../../assets/js/jquery.js";
import {properties} from '../../../environments/properties';

export function handleModalFocus(modalID) {
    $(`#${modalID}`).on('shown.bs.modal', () => {
        $('.modal-header button').focus();
        let buttons = document.querySelectorAll(`.modal-open .modal[role="dialog"]#${modalID} button`);
        buttons = Array.prototype.slice.call(buttons);
        buttons[buttons.length - 1].addEventListener('blur', () => {
            (<HTMLElement>buttons[0]).focus();
        });
    });
}

export function handleModalAnchorFocus(modalID) {
    $(`#${modalID}`).on('shown.bs.modal', () => {
        let buttons = document.querySelectorAll(`.modal-open .modal[role="dialog"]#${modalID} button`);
        buttons = Array.prototype.slice.call(buttons);
        buttons[0].addEventListener('blur', () => {
            $('.modal-footer a').focus();
        });
    });
}

export const _windowUtil = (window: any) => {
  function getWindowLocationHref(): string {
      return window.location.href;
  }
  function setWindowLocationHref(path: string): void {
      window.location.href = path;
  }

  function getWindowSearchParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  function getWindowLocationSearch(): string {
    return window.location.search;
  }

  function setWindowAssign(url): void {
    window.location.assign(url)
  }

  function openInNewWindowTab(url: string, target: string = '_blank'): void {
    window.open(url, target);
  }

  return {
      getWindowLocationHref: getWindowLocationHref,
      setWindowLocationHref: setWindowLocationHref,
      getWindowSearchParams: getWindowSearchParams,
      getWindowLocationSearch: getWindowLocationSearch,
      setWindowAssign: setWindowAssign,
      openInNewWindowTab: openInNewWindowTab
  }
}

export const findNestedObj = (obj, keyToFind) => {
  let foundObj;
  JSON.stringify(obj, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind]) {
      foundObj = nestedValue[keyToFind];
    }
    return nestedValue
  });
  return foundObj;
};

export const pluralizeBannerName = (banner: string): string => {
  banner = banner[0].toUpperCase() + banner.slice(1);
  if (banner.endsWith('s')) {
    return banner;
  }
  return `${banner}'s`;
}

export const capitalizedBannerName = (banner: string): string => {
  return (properties.bannerConfiguration as any)[banner]?.banner
    || banner[0].toUpperCase() + banner.slice(1);
}

export const capitalize = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

// Escape the five HTML-significant characters so a config/AEM-derived value can be safely
// interpolated into an HTML string that is later trusted via bypassSecurityTrustHtml.
export const escapeHtml = (value: string): string => {
  const map: { [key: string]: string } = {
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '&': '&amp;',
  };
  return (value || '').replace(/[<>"'&]/g, char => map[char]);
}

// Allow only empty, relative (/, #) or http(s) URLs; anything else (e.g. javascript:) becomes
// empty so a poisoned config value cannot inject an executable href. The result is
// HTML-escaped so it is safe to drop into an attribute value.
export const sanitizeLinkUrl = (url: string): string => {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return '';
  }
  const isSafe = trimmed.startsWith('/')
    || trimmed.startsWith('#')
    || /^https?:\/\//i.test(trimmed);
  return isSafe ? escapeHtml(trimmed) : '';
}

// Helper functions for digit validation (no regex)
const isAllDigits = (str: string): boolean => {
  return str.length > 0 && [...str].every(char => char >= '0' && char <= '9');
}

const isTwoDigits = (str: string): boolean => {
  return str.length === 2 && [...str].every(char => char >= '0' && char <= '9');
}

// Main currency conversion function
export const convertCurrencyToWords = (text, specialCase?) => {
  let newtext = "";
  if (text) {
    newtext = removeHtmlTagsSafely(text);
    newtext = convertCurrencyStringSafe(newtext);
    newtext = newtext.replace(/\//g, ' per ');

    if (specialCase && specialCase.toLowerCase() === 'discountcode') {
      newtext = handleDiscountCodeSafe(newtext);
    }
  }
  return newtext;
}

// Safely remove HTML tags using DOM
const removeHtmlTagsSafely = (text: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return tempDiv.textContent || tempDiv.innerText || '';
}

// Convert currency strings without regex - FIXED VERSION
const convertCurrencyStringSafe = (text: string): string => {
  let result = '';
  let i = 0;
  
  while (i < text.length) {
    if (text[i] === '$') {
      // Found a dollar sign, parse the currency
      const [currencyText, newIndex] = parseCurrencyAt(text, i);
      result += currencyText;
      i = newIndex;
    } else {
      result += text[i];
      i++;
    }
  }
  
  return result;
}

// Helper function to parse currency starting at a given index
const parseCurrencyAt = (text: string, startIndex: number): [string, number] => {
  let i = startIndex + 1; // Skip the $
  let dollars = '';
  
  // Parse digits for dollars
  while (i < text.length && text[i] >= '0' && text[i] <= '9') {
    dollars += text[i];
    i++;
  }
  
  // Check if we have a decimal point followed by exactly 2 digits
  if (i < text.length - 2 && text[i] === '.' && 
      text[i + 1] >= '0' && text[i + 1] <= '9' &&
      text[i + 2] >= '0' && text[i + 2] <= '9') {
    
    const cents = text[i + 1] + text[i + 2];
    const dollarsWord = dollars === '1' ? 'dollar' : 'dollars';
    return [`${dollars} ${dollarsWord} ${cents} cents`, i + 3];
  } else if (dollars.length > 0) {
    // Just dollars, no cents
    const dollarsWord = dollars === '1' ? 'dollar' : 'dollars';
    return [`${dollars} ${dollarsWord}`, i];
  } else {
    // Invalid currency format, just return the $ as-is
    return ['$', startIndex + 1];
  }
}

// Handle discount code special case
const handleDiscountCodeSafe = (text: string): string => {
  // Remove "Just" prefix safely
  if (text.toLowerCase().startsWith('just ')) {
    text = text.substring(5);
  }
  
  return replaceDollarPattern(text);
}

// Replace dollar pattern without regex
const replaceDollarPattern = (text: string): string => {
  const words = text.split(' ');
  const result = [];
  
  for (let i = 0; i < words.length; i++) {
    // Look for pattern: "number dollars number dollars"
    if (i < words.length - 3 &&
        isAllDigits(words[i]) && 
        words[i + 1] === 'dollars' &&
        isAllDigits(words[i + 2]) && 
        words[i + 3] === 'dollars') {
      
      // Add the replacement pattern
      result.push('Annual Plan Discount Price', words[i], words[i + 1], 'Original price', words[i + 2], words[i + 3]);
      i += 3;
    } else {
      result.push(words[i]);
    }
  }
  
  return result.join(' ');
}

export const loadExtFiles = async (data: { css?: string[]; scripts?: string[] }) => {
  data?.css?.forEach(href => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  });

  if (!data?.scripts?.length) return;

  data?.['scripts']?.forEach((scriptUrl) => {
    if (document.querySelector(`script[src="${scriptUrl}"]`)) return;
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true; // defer ignored for dynamically added scripts
    script.onload = () => { };
    script.onerror = () => { console.error('Failed to load', scriptUrl); };
    document.body.append(script);
  });
};